import React, { memo } from 'react';
import { formatCurrency } from '../helpers';

const FORMAS_PAGO = [
    'Transferencia Bancaria', 'Depósito', 'Cheque', 'Efectivo', 'Tarjeta de Crédito',
    'Débito Automático', 'Pagos mis cuentas', 'Mercado Pago', 'Compensación de Deuda', 'Comisiones de Terceros'
];

const ESTATUS_FACTURA = ['Pendiente de Cobro', 'Cobrada', 'Anulada'];
const TIPOS_RETENCIONES_DISPONIBLES = ['Ganancias', 'IIBB', 'IVA', 'OSSEG', 'Otras Retenciones'];

const SeccionTesoreriaFactura = ({
    formData,
    setFormData,
    retencionesList = [],
    retencionInput,
    setRetencionInput,
    calculos,
    cuentas = [],
    handleBlurCurrency,
    handleBlurMontoRetencion,
    handleCuentaChange,
    handleAddRetencion,
    handleRemoveRetencion
}) => {
    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                Tesorería, Retenciones y Estatus
            </legend>
            
            <div className="space-y-6 pt-2">
                
                {/* BLOQUE RETENCIONES MÚLTIPLES */}
                <div className="p-5 bg-rose-50/35 rounded-xl border border-rose-300 space-y-3">
                    <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                        <span className="text-[14px] font-black text-rose-700 uppercase tracking-wider">Retenciones Sufridas</span>
                        <span className="text-sm font-mono font-bold text-rose-700 bg-rose-50 px-3 py-0.5 rounded-md border border-rose-200">
                            Total Retenido: {formatCurrency(calculos.retencionesTotales, calculos.monedaSimbolo)}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div>
                            <label className="block text-[12px] font-bold text-slate-600 uppercase mb-1">Tipo de Retención</label>
                            <select
                                value={retencionInput.tipo}
                                onChange={e => setRetencionInput(prev => ({ ...prev, tipo: e.target.value }))}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 bg-white h-[46px] outline-none cursor-pointer"
                            >
                                <option value="">Seleccionar Tipo...</option>
                                {TIPOS_RETENCIONES_DISPONIBLES.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="block text-[12px] font-bold text-slate-600 uppercase mb-1">Monto ({formData.moneda})</label>
                            <input
                                type="text"
                                value={retencionInput.monto}
                                onChange={e => setRetencionInput(prev => ({ ...prev, monto: e.target.value }))}
                                onBlur={handleBlurMontoRetencion}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-base font-mono font-bold text-slate-900 bg-white h-[46px] outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                placeholder="0,00"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={handleAddRetencion}
                            className="h-[46px] px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold uppercase text-xs transition-all shrink-0 cursor-pointer shadow-xs"
                        >
                            + Agregar
                        </button>
                    </div>

                    {retencionesList.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                            {retencionesList.map((ret, idx) => (
                                <span key={idx} className="inline-flex items-center gap-2 bg-white border border-rose-200 text-slate-800 font-bold px-3 py-1.5 rounded-xl text-xs shadow-sm">
                                    <span className="text-rose-700 uppercase font-black">{ret.tipo}:</span>
                                    <span className="font-mono text-sm">{formatCurrency(ret.monto, calculos.monedaSimbolo)}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveRetencion(idx)}
                                        className="text-rose-400 hover:text-rose-700 font-black ml-1 text-sm transition-colors cursor-pointer"
                                    >
                                        ✕
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* INGRESOS A BANCO Y MEDIOS DE PAGO */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Forma de Pago</label>
                        <select 
                            value={formData.formaPago || ''} 
                            onChange={e => setFormData(prev => ({ ...prev, formaPago: e.target.value }))} 
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                        >
                            <option value="">Seleccionar...</option>
                            {FORMAS_PAGO.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Cuenta Bancaria</label>
                        <select 
                            value={formData.cuentaId || ''} 
                            onChange={handleCuentaChange} 
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                        >
                            <option value="">Seleccionar Cuenta Bancaria...</option>
                            {cuentas.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.nombreDisplay || c.nombre || `${c.banco} • ${c.titular || ''} (${c.tipoCuenta || ''})`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Ingreso Banco ({formData.moneda})</label>
                        <input
                            type="text"
                            value={formData.montoIngresoBanco || ''}
                            onChange={e => setFormData(prev => ({ ...prev, montoIngresoBanco: e.target.value }))}
                            onBlur={() => handleBlurCurrency('montoIngresoBanco')}
                            className="w-full border border-blue-500 rounded-xl px-4 py-2.5 text-base font-mono font-bold text-blue-900 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                            placeholder="0,00"
                        />
                    </div>
                </div>

                {/* DASHBOARD AUDITORÍA DE COBRO */}
                <div className="p-5 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50 shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-indigo-100 pb-2.5">
                        <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </span>
                            <span className="text-xs font-black text-slate-800 uppercase tracking-wider">Cálculo Esperado en Tesorería ({formData.moneda})</span>
                        </div>
                        <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase tracking-widest">
                            Auditoría de Cobro
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-xs">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Cobro Esperado en CBU</span>
                            <span className="font-mono text-lg font-black text-indigo-900 mt-1">
                                {formatCurrency(calculos.cobroEsperadoBanco, calculos.monedaSimbolo)}
                            </span>
                            <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                Total ({formatCurrency(calculos.montoTotal, calculos.monedaSimbolo)}) - Ret. ({formatCurrency(calculos.retencionesTotales, calculos.monedaSimbolo)})
                            </span>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-xs">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Ingreso Real en Banco</span>
                            <span className="font-mono text-lg font-black text-slate-900 mt-1">
                                {formData.montoIngresoBanco !== '' ? formatCurrency(calculos.ingresoRealBanco, calculos.monedaSimbolo) : '---'}
                            </span>
                            <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                Monto asentado en extracto
                            </span>
                        </div>

                        <div className="flex flex-col justify-center">
                            {formData.montoIngresoBanco === '' ? (
                                <div className="h-full px-4 py-3 rounded-xl bg-slate-100/80 text-slate-500 text-xs font-bold uppercase tracking-wider text-center border border-slate-200 flex items-center justify-center">
                                    Sin Ingreso a Banco
                                </div>
                            ) : (
                                <div className={`h-full px-4 py-3 rounded-xl border text-center font-mono font-black text-xs shadow-xs flex flex-col items-center justify-center ${
                                    calculos.diferenciaCobro === 0
                                        ? 'bg-emerald-50/70 text-emerald-800 border-emerald-500'
                                        : calculos.diferenciaCobro < 0
                                            ? 'bg-rose-500 text-white border-rose-600'
                                            : 'bg-amber-500 text-white border-amber-600'
                                }`}>
                                    {calculos.diferenciaCobro === 0 ? (
                                        <span className="text-sm uppercase tracking-wider flex items-center gap-1">
                                            ✓ Conciliado 100%
                                        </span>
                                    ) : (
                                        <>
                                            <span className="text-[12px] uppercase font-bold opacity-90 tracking-widest">
                                                {calculos.diferenciaCobro < 0 ? 'Faltante en Banco' : 'Excedente en Banco'}
                                            </span>
                                            <span className="text-lg font-mono font-black mt-0.5">
                                                {formatCurrency(Math.abs(calculos.diferenciaCobro), calculos.monedaSimbolo)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estatus de Cobro *</label>
                    <select 
                        value={formData.estatus} 
                        onChange={e => setFormData(prev => ({ ...prev, estatus: e.target.value }))} 
                        className={`w-full border rounded-xl px-4 py-2.5 text-sm font-black outline-none h-[46px] transition-all uppercase cursor-pointer ${
                            formData.estatus === 'Cobrada' 
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300 focus:ring-emerald-200' 
                                : formData.estatus === 'Anulada'
                                    ? 'bg-rose-50 text-rose-800 border-rose-300 focus:ring-rose-200'
                                    : 'bg-amber-50 text-amber-800 border-amber-300 focus:ring-amber-200'
                        }`}
                    >
                        {ESTATUS_FACTURA.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                {formData.estatus === 'Cobrada' && (
                    <div className="animate-fade-in pt-2">
                        <label className="block text-[14px] font-bold text-emerald-800 mb-1.5 pl-1">Fecha Real del Ingreso al Banco *</label>
                        <input 
                            type="date" 
                            required 
                            value={formData.fechaIngresoBanco || ''} 
                            onChange={e => setFormData(prev => ({ ...prev, fechaIngresoBanco: e.target.value }))} 
                            className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-emerald-50/20 h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" 
                        />
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default memo(SeccionTesoreriaFactura);